import { useEffect, useState, type FormEvent, type ReactElement } from "react";
import type { ConciergeConfigPatch, ConciergeConfigResponse } from "@kuroo/contracts";
import { KurooApiError, type KurooClient } from "@kuroo/sdk";

type ConciergePageProps = {
  client: KurooClient;
  expire: () => void;
  navigate: (path: string) => void;
};

function pageError(reason: unknown): string {
  return reason instanceof Error ? reason.message : "La API no está disponible.";
}

function isUnauthenticated(reason: unknown): boolean {
  return reason instanceof KurooApiError && reason.code === "UNAUTHENTICATED";
}

function dateLabel(value: string | null): string {
  if (!value) return "Nunca";
  try { return new Intl.DateTimeFormat("es-ES", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)); }
  catch { return value; }
}

function providerStatusLabel(status: ConciergeConfigResponse["provider"]["status"]): string {
  switch (status) {
    case "active": return "Configurado";
    case "disabled": return "Deshabilitado";
    case "error": return "Revisar conexión";
    default: return "Sin configurar";
  }
}

export function ConciergePage({ client, expire, navigate: _navigate }: ConciergePageProps): ReactElement {
  const [config, setConfig] = useState<ConciergeConfigResponse>();
  const [enabled, setEnabled] = useState(true);
  const [n8nWebhookUrl, setN8nWebhookUrl] = useState("");
  const [n8nWorkflowId, setN8nWorkflowId] = useState("");
  const [monthlyTokenLimit, setMonthlyTokenLimit] = useState(100_000);
  const [maxOutputTokens, setMaxOutputTokens] = useState(800);
  const [maxToolSteps, setMaxToolSteps] = useState(12);
  const [requestTimeoutMs, setRequestTimeoutMs] = useState(90_000);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [message, setMessage] = useState<string>();

  const hydrate = (next: ConciergeConfigResponse) => {
    setConfig(next);
    setEnabled(next.enabled);
    setN8nWebhookUrl(next.n8nWebhookUrl ?? "");
    setN8nWorkflowId(next.n8nWorkflowId ?? "");
    setMonthlyTokenLimit(next.monthlyTokenLimit);
    setMaxOutputTokens(next.maxOutputTokens);
    setMaxToolSteps(next.maxToolSteps);
    setRequestTimeoutMs(next.requestTimeoutMs);
  };

  const load = () => {
    setLoading(true);
    setError(undefined);
    void client.conciergeConfig().then(hydrate).catch((reason: unknown) => {
      if (isUnauthenticated(reason)) expire();
      setError(pageError(reason));
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const patch: ConciergeConfigPatch = {
      enabled,
      n8nWebhookUrl: n8nWebhookUrl.trim() || null,
      n8nWorkflowId: n8nWorkflowId.trim() || null,
      monthlyTokenLimit: Number(monthlyTokenLimit),
      maxOutputTokens: Number(maxOutputTokens),
      maxToolSteps: Number(maxToolSteps),
      requestTimeoutMs: Number(requestTimeoutMs)
    };
    setBusy(true);
    setError(undefined);
    setMessage(undefined);
    try {
      hydrate(await client.updateConciergeConfig(patch));
      setMessage("Configuración del Concierge guardada. Se aplicará en el próximo turno.");
    } catch (reason) {
      if (isUnauthenticated(reason)) expire();
      setError(pageError(reason));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="state state-loading" role="status"><span className="spinner" aria-hidden="true" />Cargando Concierge IA…</div>;
  if (!config) return <div className="state state-error" role="alert"><strong>No se pudo cargar Concierge IA</strong><span>{error ?? "Reintentá para volver a consultar la configuración."}</span><button className="button secondary" onClick={load}>Reintentar</button></div>;

  const provider = config.provider;
  const deploymentBlocked = !config.deploymentEnabled;
  const effectiveLabel = config.effectiveEnabled ? "Activo" : deploymentBlocked ? "Bloqueado por despliegue" : "Desactivado";

  return <div className="concierge-page">
    <header className="page-header">
      <div><p className="eyebrow">Inteligencia</p><h2>Concierge IA</h2><p className="lede">n8n ejecuta el razonamiento y las herramientas; Kuroo conserva el estado, la seguridad y la autoridad de reservas.</p></div>
      <div className="button-row"><button className="button secondary" onClick={load} disabled={busy}>Actualizar</button><button className="button primary" form="concierge-config-form" disabled={busy}>{busy ? "Guardando…" : "Guardar configuración"}</button></div>
    </header>
    {message && <div className="toast" role="status">{message}</div>}
    {error && <div className="alert error" role="alert">{error}</div>}

    <div className="concierge-grid">
      <section className="panel concierge-card concierge-status-card">
        <div className="panel-heading"><div><h3>Estado del servicio</h3><span className="subtle">Ambiente {provider.environment}</span></div><span className={`tag ${config.effectiveEnabled ? "active" : "muted"}`}>{effectiveLabel}</span></div>
        <p className="muted">El workflow se sincroniza con el script de despliegue. La API key de administración de n8n no se guarda en Kuroo ni se muestra aquí.</p>
        <div className="concierge-status-list">
          <div><span>Motor</span><strong className={`status-value ${provider.status}`}>n8n · {providerStatusLabel(provider.status)}</strong></div>
          <div><span>Workflow</span><strong>{provider.workflowId ?? "No indicado"}</strong></div>
          <div><span>Webhook</span><code>{provider.webhookUrl ?? "No indicado"}</code></div>
          <div><span>Última prueba</span><strong>{dateLabel(provider.lastTestedAt)}{provider.lastTestStatus ? ` · ${provider.lastTestStatus}` : " · Validación externa"}</strong></div>
        </div>
        {provider.lastError && <div className="concierge-inline-error">{provider.lastError}</div>}
        <p className="field-help">El secreto de ejecución se configura como secret del Worker y como credencial Header Auth en n8n. Nunca viaja al navegador.</p>
        {deploymentBlocked && <p className="field-help warning">El Worker está bloqueado por CONCIERGE_ENABLED=false. Guardar la activación no lo habilita hasta cambiar ese kill switch en el despliegue.</p>}
      </section>

      <form id="concierge-config-form" className="panel concierge-card" onSubmit={(event) => void save(event)}>
        <div className="panel-heading"><div><h3>Configuración operativa</h3><span className="subtle">Sólo administradores</span></div><span className={`tag ${config.source === "database" ? "active" : "warning"}`}>{config.source === "database" ? "Guardada" : "Valores base"}</span></div>
        <label className="concierge-toggle"><input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} disabled={busy} /><span><strong>Habilitar Concierge desde el backoffice</strong><small>{deploymentBlocked ? "El kill switch de despliegue lo mantiene bloqueado." : "Los cambios se aplican al próximo turno."}</small></span></label>
        <div className="concierge-field-grid">
          <label>URL del webhook n8n<input type="url" value={n8nWebhookUrl} onChange={(event) => setN8nWebhookUrl(event.target.value)} maxLength={2_048} placeholder="https://n8n.example.com/webhook/kuroo-concierge" /><small>HTTPS; no incluyas credenciales en la URL.</small></label>
          <label>ID explícito del workflow<input value={n8nWorkflowId} onChange={(event) => setN8nWorkflowId(event.target.value)} maxLength={160} placeholder="kuroo-concierge-v1" /><small>Se usa para verificar que el gateway llama al workflow esperado.</small></label>
        </div>
        <div className="concierge-number-grid"><label>Límite mensual (tokens)<input type="number" min={1_000} max={10_000_000} step={1_000} value={monthlyTokenLimit} onChange={(event) => setMonthlyTokenLimit(Number(event.target.value))} /></label><label>Salida máxima (tokens)<input type="number" min={128} max={4_000} value={maxOutputTokens} onChange={(event) => setMaxOutputTokens(Number(event.target.value))} /></label><label>Pasos de herramientas<input type="number" min={1} max={24} value={maxToolSteps} onChange={(event) => setMaxToolSteps(Number(event.target.value))} /></label></div>
        <label>Timeout por turno (milisegundos)<input type="number" min={5_000} max={120_000} step={1_000} value={requestTimeoutMs} onChange={(event) => setRequestTimeoutMs(Number(event.target.value))} /><small>Incluye la ejecución de n8n y sus llamadas al API/MCP de Kuroo.</small></label>
      </form>
    </div>
  </div>;
}
